import { useState } from "react";
import useSWR from "swr";
import { Check, CheckCircle2, Loader2, MessageCircle, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg, fetcher } from "@/lib/api";

export default function Suscribirse() {
  const { data: outlets } = useSWR("/public/outlets", fetcher);
  const [form, setForm] = useState({ email: "", name: "", consent: false });
  const [selected, setSelected] = useState([]);
  const [topics, setTopics] = useState([]);
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  const chosen = (outlets || []).filter((o) => selected.includes(o.id));
  const availableTopics = [...new Set(chosen.flatMap((o) => o.categories || []))];

  const toggle = (list, setList, value) =>
    setList(list.includes(value) ? list.filter((x) => x !== value) : [...list, value]);

  const submit = async (e) => {
    e.preventDefault();
    if (!selected.length) return setError("Elija al menos un medio.");
    if (!form.consent) return setError("Debe autorizar el envío de correos.");
    setState("sending");
    setError("");
    try {
      await api.post("/public/subscribe", {
        email: form.email,
        name: form.name,
        outlet_ids: selected,
        categories: topics,
        consent: true,
      });
      setState("done");
    } catch (err) {
      setError(errMsg(err));
      setState("idle");
    }
  };

  if (state === "done") {
    return (
      <div className="min-h-screen grid place-items-center p-6 relative z-10">
        <div className="max-w-lg w-full text-center rise" data-testid="subscribe-success">
          <CheckCircle2 className="h-10 w-10 text-primary mx-auto mb-4" />
          <h1 className="text-3xl font-bold tracking-tight">¡Suscripción confirmada!</h1>
          <p className="text-sm text-muted-foreground mt-3">
            Recibirá el resumen de noticias en {form.email}. Únase también a nuestros canales para
            recibirlas al instante.
          </p>
          <div className="mt-8 space-y-5">
            {chosen.map((o) => (
              <div key={o.id} className="border border-border bg-card p-5" data-testid={`channels-${o.id}`}>
                {o.logo_url ? (
                  <img src={o.logo_url} alt={o.name} className="h-9 mx-auto object-contain mb-4" />
                ) : (
                  <div className="kicker mb-3">{o.name}</div>
                )}
                <div className="flex flex-wrap justify-center gap-2">
                  {o.social?.whatsapp && (
                    <Button asChild size="sm" variant="outline" data-testid={`wa-${o.id}`}>
                      <a href={o.social.whatsapp} target="_blank" rel="noreferrer">
                        <MessageCircle className="h-3.5 w-3.5 mr-1.5" />Canal de WhatsApp
                      </a>
                    </Button>
                  )}
                  {o.social?.telegram && (
                    <Button asChild size="sm" variant="outline" data-testid={`tg-${o.id}`}>
                      <a href={o.social.telegram} target="_blank" rel="noreferrer">
                        <Send className="h-3.5 w-3.5 mr-1.5" />Canal de Telegram
                      </a>
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-10 leading-relaxed">
            Agencia Multimedios Sociedad de la Información, SRL · Puede cancelar su suscripción en
            cualquier momento con el enlace incluido en cada correo.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative z-10 py-12 px-6">
      <form onSubmit={submit} className="max-w-2xl mx-auto rise" data-testid="subscribe-form">
        <div className="kicker text-primary">Agencia Multimedios Sociedad de la Información, SRL</div>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mt-2">
          Reciba las noticias que le importan
        </h1>
        <p className="text-base text-muted-foreground mt-4 max-w-xl">
          Elija los medios y los temas de su interés. Le enviaremos un resumen con las noticias del día
          y podrá cancelar cuando quiera.
        </p>

        <div className="mt-10">
          <Label className="kicker">1 · Elija los medios</Label>
          <div className="grid sm:grid-cols-2 gap-4 mt-3">
            {(outlets || []).map((o) => {
              const on = selected.includes(o.id);
              return (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => toggle(selected, setSelected, o.id)}
                  className={`text-left border p-5 transition-all ${on ? "border-primary shadow-sm -translate-y-0.5" : "border-border hover:border-muted-foreground"}`}
                  data-testid={`outlet-option-${o.id}`}
                >
                  {o.logo_url ? (
                    <img src={o.logo_url} alt={o.name} className="h-8 object-contain mb-3" />
                  ) : (
                    <div className="font-semibold mb-2">{o.name}</div>
                  )}
                  <p className="text-xs text-muted-foreground line-clamp-2">{o.description}</p>
                  <div className={`flex items-center gap-2 mt-3 text-xs font-medium ${on ? "text-primary" : "text-muted-foreground"}`}>
                    <span className={`h-4 w-4 border grid place-items-center ${on ? "bg-primary border-primary" : "border-muted-foreground"}`}>
                      {on && <Check className="h-3 w-3 text-primary-foreground" />}
                    </span>
                    {on ? "Seleccionado" : "Seleccionar"}
                  </div>
                </button>
              );
            })}
            {!outlets && <p className="text-sm text-muted-foreground">Cargando medios…</p>}
          </div>
        </div>

        {availableTopics.length > 0 && (
          <div className="mt-10">
            <Label className="kicker">2 · Temas de interés (opcional)</Label>
            <p className="text-xs text-muted-foreground mt-1">
              Si no elige ninguno, recibirá todas las noticias de los medios seleccionados.
            </p>
            <div className="flex flex-wrap gap-2 mt-3">
              {availableTopics.map((t) => {
                const on = topics.includes(t);
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => toggle(topics, setTopics, t)}
                    className={`text-sm px-4 py-2 border rounded-full transition-colors ${on ? "bg-accent text-accent-foreground border-accent" : "border-border hover:border-muted-foreground"}`}
                    data-testid={`topic-${t}`}
                  >
                    {t}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-10 space-y-4">
          <Label className="kicker">3 · Sus datos</Label>
          <div className="grid sm:grid-cols-2 gap-4">
            <Input
              placeholder="Nombre"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="subscribe-name-input"
            />
            <Input
              type="email"
              required
              placeholder="correo@ejemplo.com"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="subscribe-email-input"
            />
          </div>
          <label className="flex items-start gap-3 text-sm cursor-pointer">
            <Checkbox
              checked={form.consent}
              onCheckedChange={(v) => setForm({ ...form, consent: !!v })}
              data-testid="subscribe-consent"
            />
            <span className="text-muted-foreground">
              Autorizo a Agencia Multimedios Sociedad de la Información, SRL a enviarme boletines
              informativos y confirmo haber leído cómo se tratan mis datos.
            </span>
          </label>
        </div>

        {error && <p className="text-sm text-destructive mt-4" data-testid="subscribe-error">{error}</p>}

        <Button type="submit" className="mt-8 h-12 px-8" disabled={state === "sending"} data-testid="subscribe-submit">
          {state === "sending" ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Enviando…</>) : "Suscribirme"}
        </Button>

        <p className="text-xs text-muted-foreground mt-10 leading-relaxed">
          PaginaUno.Do y LaSeguridadSocial.Do son propiedad y son administrados por Agencia Multimedios
          Sociedad de la Información, SRL. Para anuncios y servicios de información visite{" "}
          <a href="https://agenciamultimedios.com.do/" className="text-primary" target="_blank" rel="noreferrer">
            agenciamultimedios.com.do
          </a>
        </p>
      </form>
    </div>
  );
}
